import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { addon } from "../../types/ProductType";
import ProductAddonName from "./ProductAddonName";
import { MdOutlineDelete } from "react-icons/md";
import { AppConfig } from "@/utils/AppConfig";
import { formatMoneyForInput } from "@/utils/helpers";

type ProductAddonProps = addon & {
  onChange: (updatedAddon: addon) => void;
  error?: string;
  index: number;
  onDelete: (index: number) => void;
};

function ProductAddon({
  nameAR,
  nameEN,
  price,
  onChange,
  error,
  onDelete,
  index,
}: ProductAddonProps) {
  const { t } = useTranslation();

  // Local state for managing user input
  const [productAddonNames, setProductAddonNames] = useState({
    ar: nameAR || "",
    en: nameEN || "",
  });
  const [addonPrice, setAddonPrice] = useState<number | undefined>(price);

  // Handle changes for addon names
  const handleProductAddonNameChange = (newValues: {
    ar: string;
    en: string;
  }) => {
    setProductAddonNames(newValues);
    // Notify the parent of changes
    onChange({
      nameAR: newValues.ar,
      nameEN: newValues.en,
      price: addonPrice,
    });
  };

  const [isFoucused, setIsFocused] = useState(false);
  

  // Handle changes for addon price
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPrice = e.target.value === "" ? undefined : Number(e.target.value);
    setAddonPrice(newPrice);
    // Notify the parent of changes
    onChange({
      nameAR: productAddonNames.ar,
      nameEN: productAddonNames.en,
      price: newPrice,
    });
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-gray-300 p-3">
      <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
        <div className="flex flex-1 flex-col gap-2">
          <label className="text-sm font-semibold capitalize text-gray-700">
            {t("productAddon.name")}
          </label>
          <ProductAddonName
            productAddonNames={productAddonNames}
            onChange={handleProductAddonNameChange}
          />
        </div>

        {/* Addon Price */}
        <div className="flex w-full flex-col gap-2 sm:w-[100px] overflow-hidden">
          <label className="text-sm font-semibold capitalize text-gray-700">
          {t("productAddon.price")}
          </label>
          <div dir="rtl" className="relative size-full ">
            <input
              value={addonPrice === undefined ? "" : isFoucused ? addonPrice : formatMoneyForInput(addonPrice)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              type="number"
              placeholder= {t("productAddon.genericPrice") as string}
              className="size-full pe-[41px] h-9 rounded-md border border-gray-300 text-center"
              onChange={handlePriceChange}
            />
            <span className="text absolute end-[1px] top-1/2 h-[97%] w-[40px] flex items-center justify-center -translate-y-1/2 rounded-e-md bg-gray-100">
              {AppConfig.currency.icon()}
            </span>
          </div>
        </div>
        <button
          onClick={() => onDelete(index)}
          type="button"
          className="self-end rounded bg-red-500 px-4 py-2 font-bold text-white hover:bg-red-700"
        >
          <MdOutlineDelete size={20} />
        </button>
      </div>

      {/* Error Message */}
      {error && <div className="text-sm text-red-600">{error}</div>}
    </div>
  );
}

export default ProductAddon;